import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { aiRouteErrorCode } from "../../lib/aiRouteError.js";
import ReactMarkdown from "react-markdown";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useAiChatThread } from "../../hooks/useAiChatThread.js";
import { ChatComposer, ChatThinkingIndicator, ChatErrorBanner } from "../aiChat/index.js";
import { opAiChatMarkdownComponents } from "../../lib/opAiChatMarkdown.js";
import { isAiUsageLimitError } from "../../lib/aiRateLimit.js";
import { useToast } from "../Toast.js";
import { useConfirm } from "../ConfirmDialog.js";
import { summarizeChange, type ModelConfigChange, type ApplyModelConfigResult } from "../../lib/modelConfigChange.js";

export type ArchitectChatTenantModel = { model_id: string; status: string };

function architectModelStorageKey(operationId: string): string {
  return `atlas:architectChatModel:${operationId}`;
}

export type PipelineRecommendation = {
  stages: Array<{
    label: string;
    nodeType: string;
    purpose: string;
    changed?: boolean;
    webSearchEnabled?: boolean;
    draftPrompt?: string;
    vizType?: string;
    outputModes?: { structured: boolean; unstructured: boolean };
    maxOutputTokens?: number;
    schemaHint?: Array<{ name: string; type: string; description?: string }>;
    /** ADR-0116 §7.4 — for model_train stages: provisional, intent-bearing config
     *  written into config.spec_model_train.config by buildStageNode. */
    nodeConfig?: {
      algorithm_family?: string;
      algorithm?: string;
      validation?: { holdout_fraction?: number };
      hpo?: { enabled?: boolean };
      contamination?: number;
      n_clusters?: number;
      k_range?: [number, number];
      target_intent?: { description: string; guess?: string };
      feature_intent?: Array<{ description: string; guess?: string }>;
      target_column?: string;
      feature_columns?: string[];
      provisional?: boolean;
    };
    /** Set when this stage references a node already on the canvas. Apply will reuse
     *  the existing node by this id rather than creating a new one. */
    existingNodeId?: string;
  }>;
  connections: Array<{
    from: { stageIndex: number; port: string };
    to: { stageIndex: number; port: string };
  }>;
  /** ADR-0116 §4.3 — direct reach: concrete config deltas into existing model nodes. */
  node_changes?: Array<{ node_id: string; change: ModelConfigChange }>;
  /** ADR-0116 §4.3 — delegate: a directed instruction routed to a node's own advisor. */
  delegations?: Array<{ node_id: string; instruction: string }>;
  rationale: string;
};

type ChatLine = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  pipelineRecommendation?: PipelineRecommendation | null;
};

type ChatSegment = { kind: "text"; text: string } | { kind: "code"; text: string; lang?: string };

/** Split assistant markdown on ``` fenced blocks; remainder is conversational prose. */
function splitFencedBlocks(raw: string): ChatSegment[] {
  const re = /```([\w-]*)\s*\n?([\s\S]*?)```/g;
  const out: ChatSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) {
      const t = raw.slice(last, m.index);
      if (t.trim()) out.push({ kind: "text", text: t });
    }
    out.push({ kind: "code", text: (m[2] ?? "").replace(/\n$/, ""), lang: m[1] ? m[1] : undefined });
    last = m.index + m[0].length;
  }
  const tail = raw.slice(last);
  if (tail.trim()) out.push({ kind: "text", text: tail });
  return out.length > 0 ? out : [{ kind: "text", text: raw }];
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy failed");
}

function ClipboardIcon() {
  return (
    <svg className="opAiChatCopyIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

function CopyableCodeBlock({ label, text, onCopy }: { label: string; text: string; onCopy: () => void }) {
  const copyLabel = `Copy ${label}`;
  return (
    <div className="opAiChatCodeBlock">
      <div className="opAiChatCodeToolbar">
        <span className="opAiChatCodeLabel">{label}</span>
        <button
          type="button"
          className="btn btnSm btnTertiary opAiChatCopyIconBtn"
          aria-label={copyLabel}
          title={copyLabel}
          onClick={() => onCopy()}
        >
          <ClipboardIcon />
        </button>
      </div>
      <textarea className="opAiChatCodeTextarea" readOnly value={text} rows={Math.min(12, Math.max(3, text.split("\n").length))} />
    </div>
  );
}

function isJsonBlock(seg: ChatSegment): boolean {
  if (seg.kind !== "code") return false;
  if (seg.lang?.trim().toLowerCase() === "json") return true;
  const t = seg.text.trim();
  return (t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"));
}

function ArchitectAssistantMessageBody({ text }: { text: string }) {
  const toast = useToast();
  const segments = useMemo(
    () => splitFencedBlocks(text).filter((seg) => !isJsonBlock(seg)),
    [text]
  );

  async function handleCopy(block: string) {
    try {
      await copyToClipboard(block);
      toast.push({ kind: "success", message: "Copied" });
    } catch {
      toast.push({ kind: "error", message: "Could not copy." });
    }
  }

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {segments.map((seg, i) => {
        if (seg.kind === "text") {
          return (
            <div key={i} className="opAiChatProse">
              <ReactMarkdown components={opAiChatMarkdownComponents}>{seg.text}</ReactMarkdown>
            </div>
          );
        }
        const lang = seg.lang?.trim();
        const label = lang ? lang : "Copyable";
        return <CopyableCodeBlock key={i} label={label} text={seg.text} onCopy={() => void handleCopy(seg.text)} />;
      })}
    </div>
  );
}

function nodeTypeLabel(nodeType: string): string {
  const map: Record<string, string> = {
    provider: "SQL Provider",
    ai_provider: "AI Provider",
    transform: "Transform",
    reactive_visualization: "Reactive Viz",
    tantable: "TanTable",
    kpi_card: "KPI Card",
  };
  return map[nodeType] ?? nodeType;
}

function friendlyPortName(port: string): string {
  if (port === "tabular_out" || port === "tabular_in" || port === "data_in") return "structured";
  if (port === "content_out" || port === "context_in") return "content";
  if (port.startsWith("scope")) return "scope";
  if (port === "references_out") return "references";
  return port;
}

function stageCellText(s: PipelineRecommendation["stages"][0], idx: number): string {
  let typeInfo = nodeTypeLabel(s.nodeType);
  if (s.nodeType === "ai_provider") {
    typeInfo += `: ${s.webSearchEnabled ? "web search" : "analysis only"}`;
  }
  return `${idx + 1}. ${s.label} (${typeInfo})`;
}

function PipelineConnectionsTable({
  stages,
  connections
}: {
  stages: PipelineRecommendation["stages"];
  connections: PipelineRecommendation["connections"];
}) {
  if (connections.length === 0) return null;
  return (
    <table className="opArchitectConnectionsTable">
      <thead>
        <tr>
          <th>Source</th>
          <th>Port</th>
          <th>Target</th>
        </tr>
      </thead>
      <tbody>
        {connections.map((c, idx) => {
          const fromStage = stages[c.from.stageIndex];
          const toStage = stages[c.to.stageIndex];
          if (!fromStage || !toStage) return null;
          return (
            <tr key={idx}>
              <td>{stageCellText(fromStage, c.from.stageIndex)}</td>
              <td>{friendlyPortName(c.from.port)}</td>
              <td>{stageCellText(toStage, c.to.stageIndex)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** ADR-0116 §6.1 — business-language summary of a model stage's provisional intent
 *  (not a copyable prompt). Provisional nodes carry intent descriptions, not columns. */
function modelStageIntentText(nodeConfig: NonNullable<PipelineRecommendation["stages"][0]["nodeConfig"]>): string | null {
  const fam = nodeConfig.algorithm_family;
  const featureWords = (nodeConfig.feature_intent ?? []).map((f) => f.description).filter(Boolean);
  const featureCols = nodeConfig.feature_columns ?? [];
  const from = featureWords.length ? featureWords.join(", ") : featureCols.join(", ");
  const target = nodeConfig.target_intent?.description || nodeConfig.target_column || "";
  if (fam === "anomaly") return from ? `Flags unusual rows from ${from}.` : "Flags unusual rows.";
  if (fam === "clustering") return from ? `Groups rows into segments from ${from}.` : "Groups rows into segments.";
  if (target && from) return `Predicts ${target} from ${from}.`;
  if (target) return `Predicts ${target}.`;
  return from ? `Learns from ${from}.` : null;
}

/** ADR-0116 §6.1 — one reach delta into an existing node, with Apply via the seam. */
function NodeChangeRow({
  nodeLabel,
  nodeId,
  change,
  apply,
  resolveModelName,
}: {
  nodeLabel: string;
  nodeId: string;
  change: ModelConfigChange;
  apply: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  resolveModelName?: (id: string) => string | null;
}) {
  const toast = useToast();
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const onApply = async () => {
    setApplying(true);
    try {
      const res = await apply(nodeId, change);
      if (res.ok) setApplied(true);
      else if (!res.cancelled) toast.push({ kind: "error", message: res.reason });
    } finally {
      setApplying(false);
    }
  };
  return (
    <div className="opArchitectRecStageCard" style={{ display: "grid", gap: 6 }}>
      <div className="opArchitectRecStagePurpose">
        <strong>{nodeLabel}</strong>: {summarizeChange(change, resolveModelName)}
      </div>
      {applied ? (
        <div className="help">✓ Applied. Press Train on that node to retrain.</div>
      ) : (
        <div className="actions">
          <button type="button" className="btn btnSm btnPrimary" disabled={applying} onClick={() => void onApply()}>
            Apply change
          </button>
        </div>
      )}
    </div>
  );
}

/** ADR-0116 §6.1/§4.3 — a delegated change handed to the node's own advisor. */
function DelegationRow({
  nodeLabel,
  nodeId,
  instruction,
  onDelegate,
}: {
  nodeLabel: string;
  nodeId: string;
  instruction: string;
  onDelegate: (nodeId: string, instruction: string) => void;
}) {
  const [sent, setSent] = useState(false);
  return (
    <div className="opArchitectRecStageCard" style={{ display: "grid", gap: 6 }}>
      <div className="opArchitectRecStagePurpose">
        <strong>{nodeLabel}</strong>: {instruction}
      </div>
      {sent ? (
        <div className="help">✓ Handed to the model's advisor — see the Advisor tab on that node.</div>
      ) : (
        <div className="actions">
          <button
            type="button"
            className="btn btnSm"
            onClick={() => { onDelegate(nodeId, instruction); setSent(true); }}
            title="This needs the model's own analysis — the model's advisor will work out the change."
          >
            Ask the model's advisor
          </button>
        </div>
      )}
    </div>
  );
}

function RecommendationCard({
  rec,
  onApply,
  onApplyStage,
  onApplyNodeChange,
  onDelegate,
  nodeLabelById,
  resolveModelName,
}: {
  rec: PipelineRecommendation;
  onApply?: (rec: PipelineRecommendation) => void;
  onApplyStage?: (rec: PipelineRecommendation, stageIndex: number) => void;
  onApplyNodeChange?: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  onDelegate?: (nodeId: string, instruction: string) => void;
  nodeLabelById?: (nodeId: string) => string | null;
  resolveModelName?: (id: string) => string | null;
}) {
  const toast = useToast();

  async function copyBlock(block: string) {
    try {
      await copyToClipboard(block);
      toast.push({ kind: "success", message: "Copied" });
    } catch {
      toast.push({ kind: "error", message: "Could not copy." });
    }
  }

  // In refinement mode (any stage has changed=true), only show detail cards for changed stages.
  // Otherwise show all stages (initial recommendation).
  const isRefinement = rec.stages.some((s) => s.changed === true);
  const detailStages = isRefinement
    ? rec.stages.map((s, idx) => ({ s, idx })).filter(({ s }) => s.changed === true)
    : rec.stages.map((s, idx) => ({ s, idx }));

  return (
    <div className="opArchitectRecRoot">
      <div className="opArchitectRecHeading">
        <span>{isRefinement ? "Refined pipeline" : "Recommended pipeline"}</span>
        {onApply ? (
          <button
            type="button"
            className="btn btnSm btnPrimary"
            onClick={() => onApply(rec)}
            title="Add these nodes and connections to the canvas"
          >
            Apply to canvas
          </button>
        ) : null}
      </div>
      <PipelineConnectionsTable stages={rec.stages} connections={rec.connections} />
      <div className="opArchitectRecStageList">
        {detailStages.map(({ s, idx }) => {
          const dp = s.draftPrompt?.trim();
          return (
            <div key={idx} className="opArchitectRecStageCard">
              <div className="opArchitectRecStageHeader">
                <span className="opArchitectRecStageNum">{idx + 1}</span>
                <span className="opArchitectRecStageName">{s.label}</span>
                <span className="opArchitectRecStageType">{nodeTypeLabel(s.nodeType)}</span>
                {s.nodeType === "ai_provider" ? (
                  <span className={`opArchitectWebBadge${s.webSearchEnabled ? " opArchitectWebBadgeOn" : ""}`}>
                    {s.webSearchEnabled ? "web search" : "analysis only"}
                  </span>
                ) : null}
                {onApplyStage ? (
                  <button
                    type="button"
                    className="btn btnSm"
                    onClick={() => onApplyStage(rec, idx)}
                    title="Add this node to the canvas"
                  >
                    Add to canvas
                  </button>
                ) : null}
              </div>
              <div className="opArchitectRecStagePurpose">{s.purpose}</div>
              {s.nodeConfig ? (
                (() => {
                  const intent = modelStageIntentText(s.nodeConfig);
                  return intent ? <div className="help">{intent}</div> : null;
                })()
              ) : null}
              {dp ? (
                <CopyableCodeBlock
                  label="Starting prompt"
                  text={dp}
                  onCopy={() => void copyBlock(dp)}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {rec.node_changes && rec.node_changes.length > 0 && onApplyNodeChange ? (
        <div className="opArchitectRecStageList" style={{ marginTop: 8 }}>
          <div className="label">Changes to existing nodes</div>
          {rec.node_changes.map((nc, i) => (
            <NodeChangeRow
              key={`nc-${i}`}
              nodeId={nc.node_id}
              nodeLabel={nodeLabelById?.(nc.node_id) ?? "this model"}
              change={nc.change}
              apply={onApplyNodeChange}
              resolveModelName={resolveModelName}
            />
          ))}
        </div>
      ) : null}

      {rec.delegations && rec.delegations.length > 0 && onDelegate ? (
        <div className="opArchitectRecStageList" style={{ marginTop: 8 }}>
          <div className="label">Needs the model's own analysis</div>
          {rec.delegations.map((d, i) => (
            <DelegationRow
              key={`dg-${i}`}
              nodeId={d.node_id}
              nodeLabel={nodeLabelById?.(d.node_id) ?? "this model"}
              instruction={d.instruction}
              onDelegate={onDelegate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ArchitectChat(props: {
  operationId: string;
  aiChatBlocked: boolean;
  onAiChatRateLimited: () => void;
  onApplyPipeline?: (rec: PipelineRecommendation) => void;
  onApplyStage?: (rec: PipelineRecommendation, stageIndex: number) => void;
  /** ADR-0116 — apply a reach delta into an existing model node (the seam). */
  onApplyNodeChange?: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  /** ADR-0116 — route a delegated instruction to a node's own advisor. */
  onDelegate?: (nodeId: string, instruction: string) => void;
  /** ADR-0116 — resolve a node id to its label (never show a UUID). */
  nodeLabelById?: (nodeId: string) => string | null;
  /** ADR-0116 — resolve a model id to its name (never show a UUID in a binding diff). */
  resolveModelName?: (id: string) => string | null;
  tenantAiModels: ArchitectChatTenantModel[];
  accountDefaultAiModelId: string;
  /** Inspector panel: taller scroll area. */
  layout?: "default" | "inspector";
}) {
  const {
    operationId,
    aiChatBlocked,
    onAiChatRateLimited,
    onApplyPipeline,
    onApplyStage,
    onApplyNodeChange,
    onDelegate,
    nodeLabelById,
    resolveModelName,
    tenantAiModels,
    accountDefaultAiModelId,
    layout = "default"
  } = props;
  const toast = useToast();
  const confirm = useConfirm();
  const {
    sessionId,
    setSessionId,
    messages: lines,
    setMessages: setLines,
    replyPending,
    refresh: refreshChat,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread<ChatLine>({
    threadKey: `${operationId}/architect`,
    loadLatest: useCallback(async () => {
      const data = await apiFetch<{
        session: { id: string; created_at?: string };
        messages: Array<{
          id: string;
          role: "user" | "assistant" | "system";
          content: string;
          created_at?: string;
          pipeline_recommendation?: PipelineRecommendation | null;
        }>;
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/ai/architect/sessions/latest`);
      const out: ChatLine[] = [];
      for (const m of data.messages) {
        if (m.role === "user") {
          out.push({ id: m.id, role: "user", content: m.content, created_at: m.created_at ?? "" });
        } else if (m.role === "assistant") {
          out.push({
            id: m.id,
            role: "assistant",
            content: m.content,
            created_at: m.created_at ?? "",
            pipelineRecommendation: m.pipeline_recommendation ?? null,
          });
        }
      }
      return { sessionId: data.session.id, messages: out };
    }, [operationId]),
    createNewSession: useCallback(async () => {
      const data = await apiFetch<{ session: { id: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/ai/architect/sessions`,
        { method: "POST", body: {} }
      );
      return { sessionId: data.session.id };
    }, [operationId]),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ADR-0207 §3.4: the hub's error code, for the admin's link under an AI-route refusal.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [architectModelId, setArchitectModelId] = useState("");

  const selectableModels = useMemo(
    () => tenantAiModels.filter((m) => m.status === "active" || m.status === "deprecated"),
    [tenantAiModels]
  );

  const architectOrphanModel =
    architectModelId && !selectableModels.some((m) => m.model_id === architectModelId)
      ? architectModelId
      : null;

  useEffect(() => {
    if (selectableModels.length === 0) {
      setArchitectModelId("");
      return;
    }
    let stored: string | undefined;
    try {
      stored = sessionStorage.getItem(architectModelStorageKey(operationId))?.trim();
    } catch {
      /* ignore */
    }
    const storedOk = stored && selectableModels.some((m) => m.model_id === stored);
    if (storedOk) {
      setArchitectModelId(stored!);
      return;
    }
    setArchitectModelId((prev) => {
      if (prev && selectableModels.some((m) => m.model_id === prev)) return prev;
      const def = accountDefaultAiModelId.trim();
      if (def && selectableModels.some((m) => m.model_id === def)) return def;
      return selectableModels[0]!.model_id;
    });
  }, [operationId, selectableModels, accountDefaultAiModelId]);

  function handleArchitectModelPillChange(next: string) {
    setArchitectModelId(next);
    try {
      sessionStorage.setItem(architectModelStorageKey(operationId), next);
    } catch {
      /* ignore */
    }
  }

  const scrollBoxStyle = useMemo(
    () =>
      // ADR-0129 — in the inspector (spine AI slot) the thread flexes to fill the
      // frame and the composer pins to the bottom, matching the other AI slots;
      // the default (page) layout keeps its capped flowing box.
      (layout === "inspector"
        ? { flex: 1, minHeight: 0, overflowY: "auto" as const, background: "var(--bg-subtle)" }
        : { minHeight: 100, maxHeight: 340, overflowY: "auto" as const, background: "var(--bg-subtle)" }) satisfies CSSProperties,
    [layout]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [lines]);

  async function ensureSession(): Promise<string> {
    if (sessionId) return sessionId;
    const data = await apiFetch<{ session: { id: string } }>(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/ai/architect/sessions`,
      { method: "POST", body: {} }
    );
    setSessionId(data.session.id);
    return data.session.id;
  }

  async function send() {
    const text = input.trim();
    if (!text || sending || aiChatBlocked || !operationId) return;
    setSending(true);
    setError(null); setErrorCode(null);
    setInput("");
    const localId = `local-${Date.now()}`;
    setLines((prev) => [...prev, { id: localId, role: "user", content: text, created_at: new Date().toISOString() }]);
    try {
      const sid = await ensureSession();
      await apiFetch<{
        assistantMessage: {
          id: string;
          displayText: string;
          pipeline_recommendation?: PipelineRecommendation | null;
        };
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/ai/architect/sessions/${encodeURIComponent(sid)}/messages`, {
        method: "POST",
        body: { content: text, ...(architectModelId.trim() ? { model: architectModelId.trim() } : {}) }
      });
      await refreshChat();
    } catch (e) {
      if (isAiUsageLimitError(e)) {
        toast.push({ kind: "error", message: e.message });
        onAiChatRateLimited();
        setInput(text);
        setLines((prev) => (prev.length && prev[prev.length - 1]?.id === localId ? prev.slice(0, -1) : prev));
      } else {
        setError(e instanceof Error ? e.message : String(e)); setErrorCode(aiRouteErrorCode(e));
        setInput(text);
        setLines((prev) => (prev.length && prev[prev.length - 1]?.id === localId ? prev.slice(0, -1) : prev));
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className={`opAiChatTab opArchitectChatRoot${layout === "inspector" ? " opArchitectChatRoot--inspector" : ""}`}
      // Override .opAiChatTab's grid with a filling flex column so the thread
      // flexes and the composer pins to the frame bottom (spine AI slot).
      style={layout === "inspector" ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 } : undefined}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        {selectableModels.length > 0 || architectOrphanModel ? (
          <select
            className="pillSelect pillSelectNeutral"
            value={architectOrphanModel ? architectOrphanModel : architectModelId}
            onChange={(e) => handleArchitectModelPillChange(e.target.value)}
            disabled={sending || aiChatBlocked}
            title="Model for architect chat"
          >
            {architectOrphanModel ? (
              <option value={architectOrphanModel}>{architectOrphanModel} (not in tenant list)</option>
            ) : null}
            {selectableModels.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.model_id}
                {m.status === "deprecated" ? " (deprecated)" : ""}
              </option>
            ))}
          </select>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="btn btnSm"
          onClick={() =>
            void (async () => {
              const ok = await confirm.confirm({
                title: "Reset this chat?",
                detail:
                  "The current conversation is cleared from view and a fresh session starts. Past messages stay on the server but are no longer visible here.",
                confirmLabel: "Reset chat",
              });
              if (!ok) return;
              await resetChat();
            })()
          }
          disabled={lines.length === 0}
          title="Start a new conversation. Past messages stay on the server but are no longer visible here."
        >
          Reset chat
        </button>
      </div>
      <ChatErrorBanner message={error ?? chatThreadError} code={error ? errorCode : null} />
      <div ref={scrollRef} className="card opAiChatScroll" style={scrollBoxStyle}>
        <div className="opAiChatThread">
          {lines.map((row) =>
            row.role === "user" ? (
              <div key={row.id} className="opAiChatRow opAiChatRowUser">
                <div className="opAiChatMeta">You</div>
                <div className="opAiChatBubbleUser">
                  <div className="opAiChatProse">{row.content}</div>
                </div>
              </div>
            ) : (
              <div key={row.id} className="opAiChatRow opAiChatRowAssistant">
                <div className="opAiChatMeta">Architect</div>
                <div className="opAiChatBubbleAssistant">
                  <ArchitectAssistantMessageBody text={row.content} />
                  {row.pipelineRecommendation ? <RecommendationCard rec={row.pipelineRecommendation} onApply={onApplyPipeline} onApplyStage={onApplyStage} onApplyNodeChange={onApplyNodeChange} onDelegate={onDelegate} nodeLabelById={nodeLabelById} resolveModelName={resolveModelName} /> : null}
                </div>
              </div>
            )
          )}
          <ChatThinkingIndicator visible={replyPending} />
        </div>
      </div>
      <ChatComposer
        draft={input}
        setDraft={setInput}
        onSend={() => void send()}
        onCancel={() => void cancelChat()}
        inFlight={sending || replyPending}
        placeholder="Describe your pipeline goal…"
        disabledExtra={aiChatBlocked}
        disabledExtraTitle="AI chat temporarily limited — try again in about a minute."
        minRows={2}
        maxRows={12}
      />
      {aiChatBlocked ? (
        <div className="help">AI chat temporarily limited — try again in about a minute.</div>
      ) : null}
    </div>
  );
}
